import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'resstro-manager-pwa-dismiss';

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    const ua = navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);

    if (isIos && isSafari) {
      setIosHint(true);
      setVisible(true);
      return;
    }

    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed z-40 inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] md:bottom-4 md:right-4 md:left-auto md:max-w-sm">
      <div className="card p-3.5 shadow-lg flex gap-3 items-start">
        <div className="mt-0.5 rounded-lg bg-orange/10 p-2 text-orange shrink-0">
          <Download size={18} />
        </div>
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold">Установить приложение</div>
          <p className="text-ink/55 text-xs mt-0.5 leading-relaxed">
            {iosHint
              ? 'В Safari: Поделиться → «На экран Домой»'
              : 'Кабинет менеджера на домашнем экране — как обычное приложение'}
          </p>
          <div className="mt-2.5 flex gap-2">
            {!iosHint && deferred ? (
              <button type="button" className="btn-primary !py-2 !text-xs" onClick={() => void install()}>
                Установить
              </button>
            ) : null}
            <button type="button" className="btn-ghost !py-2 !text-xs" onClick={dismiss}>
              Позже
            </button>
          </div>
        </div>
        <button type="button" className="text-ink/40 hover:text-ink p-1 -m-1" onClick={dismiss} aria-label="Закрыть">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
